import React, { useState } from 'react';
import {
  Box, Typography, Button, TextField, Checkbox, FormControlLabel,
  InputAdornment, IconButton, Collapse, Divider, Slider, Popover,
  ToggleButtonGroup, ToggleButton, Tooltip, CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import { APPLICATION_TYPES } from './applicationStyles';
import { getCaseStatusColor, STATUS_FILTER_GROUPS, STATUS_BADGE_LABELS } from '../utils/caseStatus';
import { useElementWidth } from '../hooks/useElementWidth';
import { FILTER_PANEL_COMFORT_W, PANEL_CHROME_HEADER_H } from '../layout/appChrome';
import { usePersistedScroll } from '../utils/usePersistedScroll';

// Theme colors based on the requested "light mode" (unlike Pic A's dark mode, matching homepage)
const colors = {
  bg: '#ffffff',
  surface: '#f8f9fa',
  border: '#e5e7eb',
  text: '#111827',
  textSecondary: '#6b7280',
  primary: '#7454A5',
  primaryHover: '#5e4385',
  primaryLight: '#f3f0fa',
};

function PillButton({ label, selected, onClick, color = colors.primary }) {
  return (
    <Button
      variant={selected ? 'contained' : 'outlined'}
      onClick={onClick}
      sx={{
        borderRadius: '20px',
        textTransform: 'none',
        fontSize: '0.75rem',
        fontWeight: 600,
        py: 0.5,
        px: 2,
        bgcolor: selected ? color : 'transparent',
        color: selected ? '#fff' : colors.textSecondary,
        borderColor: selected ? color : colors.border,
        '&:hover': {
          bgcolor: selected ? color : colors.primaryLight,
          borderColor: color,
        },
        flexGrow: 1,
        minWidth: 'fit-content'
      }}
    >
      {label}
    </Button>
  );
}

function FilterSection({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box sx={{ mb: 3 }}>
      <Box 
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', mb: 1 }}
        onClick={() => setOpen(!open)}
      >
        <Typography variant="overline" sx={{ fontWeight: 700, color: colors.textSecondary, letterSpacing: '0.05em' }}>
          {title}
        </Typography>
        {open ? <KeyboardArrowUpIcon fontSize="small" sx={{ color: colors.textSecondary }} /> : <KeyboardArrowDownIcon fontSize="small" sx={{ color: colors.textSecondary }} />}
      </Box>
      <Collapse in={open}>
        <Box sx={{ pt: 1 }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

function DealSizeSlider({ label, value, operator, onChange, max }) {
  const [localValue, setLocalValue] = useState(value || '');

  // Sync local state when external value resets (e.g., from Clear or parent)
  React.useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const handleSliderChange = (e, val) => {
    setLocalValue(val);
  };

  const handleSliderChangeCommitted = (e, val) => {
    onChange({ operator, value: val });
  };

  const handleInputChange = (e) => {
    setLocalValue(e.target.value);
  };

  const handleInputBlur = () => {
    if (localValue === '') {
      onChange({ operator, value: '' });
      return;
    }
    let parsed = parseFloat(localValue);
    if (isNaN(parsed) || parsed < 0) {
      setLocalValue(0);
      onChange({ operator, value: 0 });
    } else {
      if (parsed > max) parsed = max;
      setLocalValue(parsed);
      onChange({ operator, value: parsed });
    }
  };

  const getCommitValue = () => {
    if (localValue === '') return '';
    const parsed = parseFloat(localValue);
    return isNaN(parsed) ? '' : parsed;
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="caption" sx={{ color: colors.textSecondary, mb: 1, display: 'block', fontWeight: 600 }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
        <Slider 
          size="small"
          value={typeof localValue === 'number' ? localValue : (parseFloat(localValue) || 0)}
          min={0}
          max={max}
          onChange={handleSliderChange}
          onChangeCommitted={handleSliderChangeCommitted}
          sx={{
            color: colors.primary,
            '& .MuiSlider-thumb': {
              width: 14,
              height: 14,
              '&:hover, &.Mui-focusVisible': {
                boxShadow: `0px 0px 0px 8px ${colors.primary}20`,
              },
              '&.Mui-active': {
                boxShadow: `0px 0px 0px 14px ${colors.primary}20`,
              },
            },
          }}
        />
        <Box sx={{ minWidth: 48, height: 28 }}>
          <TextField 
            variant="outlined" 
            size="small" 
            value={localValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            placeholder="—"
            inputProps={{
              style: { textAlign: 'center', padding: '4px 6px', fontSize: '0.8rem', color: colors.text }
            }}
            sx={{
              width: '100%',
              bgcolor: colors.surface,
              '& .MuiOutlinedInput-root': {
                height: 28,
                '& fieldset': {
                  borderColor: colors.border,
                },
                '&:hover fieldset': {
                  borderColor: colors.textSecondary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: colors.primary,
                },
              },
            }}
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Button 
          variant="outlined"
          size="small"
          onClick={() => onChange({ operator: 'less', value: getCommitValue() })}
          sx={{ flex: 1, textTransform: 'none', fontSize: '0.7rem', py: 0.25, px: 0.5,
            bgcolor: operator === 'less' ? colors.surface : 'transparent',
            color: operator === 'less' ? colors.text : colors.textSecondary,
            borderColor: operator === 'less' ? colors.textSecondary : colors.border,
            boxShadow: 'none',
            '&:hover': {
              bgcolor: colors.surface,
              borderColor: colors.textSecondary,
            }
          }}
        >
          Less than
        </Button>
        <Button 
          variant="outlined"
          size="small"
          onClick={() => onChange({ operator: 'greater', value: getCommitValue() })}
          sx={{ flex: 1, textTransform: 'none', fontSize: '0.7rem', py: 0.25, px: 0.5,
            bgcolor: operator === 'greater' ? colors.surface : 'transparent',
            color: operator === 'greater' ? colors.text : colors.textSecondary,
            borderColor: operator === 'greater' ? colors.textSecondary : colors.border,
            boxShadow: 'none',
            '&:hover': {
              bgcolor: colors.surface,
              borderColor: colors.textSecondary,
            }
          }}
        >
          Greater than
        </Button>
        <Button 
          variant="outlined"
          size="small"
          onClick={() => { setLocalValue(''); onChange({ operator: '', value: '' }); }}
          sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0.25, px: 1, minWidth: 0,
            color: colors.textSecondary,
            borderColor: colors.border,
            '&:hover': {
              bgcolor: colors.surface,
              borderColor: colors.textSecondary,
            }
          }}
        >
          Clear
        </Button>
      </Box>
    </Box>
  );
}

export default function FilterPanel({
  loading = false,
  counties, // Array of { name, slug }
  selectedCountySlugs,
  onCountiesChange,
  /** { [countySlug]: Array<{ id, geoid, name }> } — published + assigned municipalities */
  municipalityOptionsBySlug = {},
  selectedMunicipalityIds = [],
  onMunicipalityIdsChange = () => {},
  /** `counties` = counties only (no municipality filter). `counties_and_municipalities` = tree + muni filter. */
  locationFilterMode = 'counties',
  onLocationFilterModeChange = () => {},

  caseTypes,
  selectedCaseTypes,
  onCaseTypesChange,

  statusOptions,
  selectedStatuses,
  onStatusesChange,

  dateRange,
  includeAllDates,
  onDateChange, // ({ includeAllDates, from, to })
  activityDateMode = 'overlap',
  onActivityDateModeChange,

  dealSize,
  onDealSizeChange,

  caseNatureOptions,
  selectedCaseNatures,
  onCaseNaturesChange,

  onReset,
  onClose,

  userSavedFilters = [],
  activeSavedFilterId = null,
  filtersModified = false,
  onLoadSavedFilter = () => {},
  onSaveCurrentFilters = async () => {},
  onRenameSavedFilter = async () => {},
  onOverwriteSavedFilter = async () => {},
  onDeleteSavedFilter = async () => {},
}) {
  const [panelRef, panelWidth] = useElementWidth();
  const narrowChrome = panelWidth > 0 && panelWidth < FILTER_PANEL_COMFORT_W;

  const [countySearch, setCountySearch] = useState('');
  const [expandedCountySlugs, setExpandedCountySlugs] = useState({});
  const [viewMenuAnchor, setViewMenuAnchor] = useState(null);
  const [creatingSavedFilter, setCreatingSavedFilter] = useState(false);
  const [newSavedFilterName, setNewSavedFilterName] = useState('');
  const [editingFilterId, setEditingFilterId] = useState(null);
  const [editingFilterName, setEditingFilterName] = useState('');
  const [savingFilter, setSavingFilter] = useState(false);
  const [overwritingFilterId, setOverwritingFilterId] = useState(null);
  const [confirmOverwriteFilterId, setConfirmOverwriteFilterId] = useState(null);
  const [confirmDeleteFilterId, setConfirmDeleteFilterId] = useState(null);
  const [deletingFilterId, setDeletingFilterId] = useState(null);

  const activeSavedFilter = userSavedFilters.find((f) => f.id === activeSavedFilterId) ?? null;

  const commitEditFilterName = async () => {
    const id = editingFilterId;
    const trimmed = editingFilterName.trim();
    if (!id) return;

    setEditingFilterId(null);
    setEditingFilterName('');

    const filter = userSavedFilters.find((f) => f.id === id);
    if (!filter) return;
    const finalName = trimmed || filter.name;
    if (finalName === filter.name) return;

    try {
      await onRenameSavedFilter(id, finalName);
    } catch {
      /* parent logs */
    }
  };

  const closeSavedFiltersMenu = () => {
    setViewMenuAnchor(null);
    setCreatingSavedFilter(false);
    setNewSavedFilterName('');
    setEditingFilterId(null);
    setEditingFilterName('');
    setConfirmDeleteFilterId(null);
    setConfirmOverwriteFilterId(null);
  };

  const handleSavedFiltersPopoverClose = () => {
    void commitEditFilterName();
    setViewMenuAnchor(null);
    setCreatingSavedFilter(false);
    setNewSavedFilterName('');
    setConfirmDeleteFilterId(null);
    setConfirmOverwriteFilterId(null);
  };

  const startEditFilterName = (filter, e) => {
    e?.stopPropagation();
    setConfirmDeleteFilterId(null);
    setConfirmOverwriteFilterId(null);
    setEditingFilterId(filter.id);
    setEditingFilterName(filter.name);
  };

  const cancelEditFilterName = () => {
    setEditingFilterId(null);
    setEditingFilterName('');
  };

  const handleSaveNewFilter = async () => {
    const name = newSavedFilterName.trim();
    if (!name || savingFilter) return;
    setSavingFilter(true);
    try {
      await onSaveCurrentFilters(name);
      setCreatingSavedFilter(false);
      setNewSavedFilterName('');
      closeSavedFiltersMenu();
    } catch {
      /* parent logs */
    } finally {
      setSavingFilter(false);
    }
  };

  const handleDeleteFilter = (filterId, e) => {
    e.stopPropagation();
    setEditingFilterId(null);
    setEditingFilterName('');
    setConfirmOverwriteFilterId(null);
    setConfirmDeleteFilterId(filterId);
  };

  const startOverwriteConfirm = (filterId, e) => {
    e.stopPropagation();
    setEditingFilterId(null);
    setEditingFilterName('');
    setConfirmDeleteFilterId(null);
    setConfirmOverwriteFilterId(filterId);
  };

  const cancelOverwriteConfirm = (e) => {
    e?.stopPropagation();
    setConfirmOverwriteFilterId(null);
  };

  const confirmOverwriteFilter = async (filterId, e) => {
    e.stopPropagation();
    if (overwritingFilterId) return;
    setOverwritingFilterId(filterId);
    try {
      await onOverwriteSavedFilter(filterId);
      setConfirmOverwriteFilterId(null);
    } catch {
      /* parent logs */
    } finally {
      setOverwritingFilterId(null);
    }
  };

  const cancelDeleteConfirm = (e) => {
    e?.stopPropagation();
    setConfirmDeleteFilterId(null);
  };

  const confirmDeleteFilter = async (filterId, e) => {
    e.stopPropagation();
    if (deletingFilterId) return;
    setDeletingFilterId(filterId);
    try {
      await onDeleteSavedFilter(filterId);
      setConfirmDeleteFilterId(null);
    } catch {
      /* parent logs */
    } finally {
      setDeletingFilterId(null);
    }
  };

  const filteredCounties = counties.filter(c => {
    const search = countySearch.toLowerCase();
    return c.name.toLowerCase().includes(search) || (c.state || '').toLowerCase().includes(search);
  });
  const useMuniTree = locationFilterMode === 'counties_and_municipalities';
  const publishedMuniIds = React.useMemo(
    () => new Set(Object.values(municipalityOptionsBySlug).flat().filter((m) => m.is_published !== false).map((m) => m.id)),
    [municipalityOptionsBySlug]
  );
  const totalMuniOptions = publishedMuniIds.size;
  const selectedPublishedCount = React.useMemo(
    () => selectedMunicipalityIds.filter((id) => publishedMuniIds.has(id)).length,
    [selectedMunicipalityIds, publishedMuniIds]
  );

  const [localDateRange, setLocalDateRange] = useState(dateRange);
  const filterScrollRef = usePersistedScroll('ls_filterPanelScroll', {
    restoreDeps: [counties.length, selectedCountySlugs.length],
  });


  // Sync local state if parent changes (e.g. reset button)
  React.useEffect(() => {
    setLocalDateRange(dateRange);
  }, [dateRange]);

  const handleDateBlur = () => {
    // Only update parent if a valid 4-digit year is present or if empty
    const isValidYear = (d) => !d || d.split('-')[0].length === 4;
    
    if (isValidYear(localDateRange.from) && isValidYear(localDateRange.to)) {
      onDateChange({ 
        includeAllDates, 
        from: localDateRange.from, 
        to: localDateRange.to 
      });
    }
  };

  const handleCountyToggle = (slug) => {
    if (selectedCountySlugs.includes(slug)) {
      onCountiesChange(selectedCountySlugs.filter(s => s !== slug));
      const drop = new Set((municipalityOptionsBySlug[slug] || []).map((m) => m.id));
      if (drop.size) {
        onMunicipalityIdsChange(selectedMunicipalityIds.filter((id) => !drop.has(id)));
      }
    } else {
      onCountiesChange([...selectedCountySlugs, slug]);
    }
  };

  const handleSelectAllCounties = () => {
    if (selectedCountySlugs.length === counties.length) {
      onCountiesChange([]);
      onMunicipalityIdsChange([]);
    } else {
      onCountiesChange(counties.map(c => c.slug));
      if (useMuniTree) {
        const allMuniIds = [...new Set(Object.values(municipalityOptionsBySlug).flat().filter((m) => m.is_published !== false).map((m) => m.id))];
        onMunicipalityIdsChange(allMuniIds);
      }
    }
  };

  const handleMunicipalityToggle = (muniId) => {
    if (selectedMunicipalityIds.includes(muniId)) {
      onMunicipalityIdsChange(selectedMunicipalityIds.filter((id) => id !== muniId));
    } else {
      onMunicipalityIdsChange([...selectedMunicipalityIds, muniId]);
    }
  };

  const handleCaseTypeToggle = (type) => {
    if (selectedCaseTypes.includes(type)) {
      onCaseTypesChange(selectedCaseTypes.filter(t => t !== type));
    } else {
      onCaseTypesChange([...selectedCaseTypes, type]);
    }
  };

  const handleStatusGroupToggle = (groupKey) => {
    const groupStatuses = STATUS_FILTER_GROUPS[groupKey] || [groupKey];
    const allSelected = groupStatuses.every((s) => selectedStatuses.includes(s));
    if (allSelected) {
      onStatusesChange(selectedStatuses.filter((s) => !groupStatuses.includes(s)));
    } else {
      const merged = new Set([...selectedStatuses, ...groupStatuses]);
      onStatusesChange([...merged]);
    }
  };

  const handleStatusToggle = (st) => {
    if (selectedStatuses.includes(st)) {
      onStatusesChange(selectedStatuses.filter((o) => o !== st));
    } else {
      onStatusesChange([...selectedStatuses, st]);
    }
  };

  const handleCaseNatureToggle = (nature) => {
    if (selectedCaseNatures.includes(nature)) {
      onCaseNaturesChange(selectedCaseNatures.filter((n) => n !== nature));
    } else {
      onCaseNaturesChange([...selectedCaseNatures, nature]);
    }
  };

  return (
    <Box
      ref={panelRef}
      sx={{ 
      width: '100%', 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: colors.bg,
      borderRight: `1px solid ${colors.border}`,
      boxShadow: '2px 0 8px rgba(0,0,0,0.02)',
      zIndex: 10,
      minWidth: 0,
      boxSizing: 'border-box',
    }}>
      {/* Header */}
      <Box
        sx={{
          px: narrowChrome ? 1.25 : 2,
          borderBottom: `1px solid ${colors.border}`,
          boxSizing: 'border-box',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: narrowChrome ? 0.75 : 1.5,
            minWidth: 0,
            width: '100%',
            minHeight: PANEL_CHROME_HEADER_H,
            height: PANEL_CHROME_HEADER_H,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexShrink: 0, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: colors.text, lineHeight: 1.2, letterSpacing: '0.05em', flexShrink: 0 }}
            >
              FILTERS
            </Typography>
            <Button
              size="small"
              onClick={onReset}
              sx={{
                textTransform: 'none',
                color: colors.textSecondary,
                fontSize: '0.75rem',
                fontWeight: 500,
                p: 0,
                minWidth: 0,
                minHeight: 0,
                height: 'auto',
                lineHeight: 1.2,
                verticalAlign: 'baseline',
                '&:hover': { color: colors.primary, bgcolor: 'transparent' },
              }}
              disableRipple
            >
              Reset
            </Button>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0, justifyContent: 'flex-end' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={narrowChrome ? undefined : <AddIcon sx={{ fontSize: '1rem !important', mr: -0.5 }} />}
              endIcon={<KeyboardArrowDownIcon sx={{ fontSize: narrowChrome ? '1rem !important' : '1.2rem !important', ml: narrowChrome ? -0.25 : -0.5 }} />}
              onClick={(e) => setViewMenuAnchor(e.currentTarget)}
              sx={{
                textTransform: 'none',
                py: 0.25,
                px: narrowChrome ? 1 : 1.5,
                color: colors.text,
                borderColor: activeSavedFilter ? colors.primary : colors.border,
                fontWeight: 600,
                borderRadius: '20px',
                fontSize: narrowChrome ? '0.7rem' : '0.75rem',
                flexShrink: 1,
                minWidth: 0,
                maxWidth: '100%',
                '& .MuiButton-endIcon': { marginLeft: narrowChrome ? 0.25 : undefined },
                '&:hover': { borderColor: colors.primary, bgcolor: colors.primaryLight },
              }}
            >
              Save/Load
            </Button>
            <Popover
              open={Boolean(viewMenuAnchor)}
              anchorEl={viewMenuAnchor}
              onClose={handleSavedFiltersPopoverClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{ paper: { sx: { minWidth: 0, width: 'min(340px, calc(100vw - 32px))', maxWidth: 340, mt: 0.5 } } }}
            >
            <Box sx={{ p: 1.5 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: colors.textSecondary, letterSpacing: '0.06em', display: 'block', mb: 1 }}
              >
                SAVED FILTERS
              </Typography>

              {userSavedFilters.length === 0 ? (
                <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary, py: 0.5, mb: 1 }}>
                  No saved filters
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mb: 1 }}>
                  {userSavedFilters.map((filter) => {
                    const isActive = filter.id === activeSavedFilterId;
                    const isEditing = editingFilterId === filter.id;
                    const isConfirmingDelete = confirmDeleteFilterId === filter.id;
                    const isConfirmingOverwrite = confirmOverwriteFilterId === filter.id;
                    const isBusy = isEditing || isConfirmingDelete || isConfirmingOverwrite;
                    return (
                      <Box
                        key={filter.id}
                        onClick={() => {
                          if (!isBusy) {
                            onLoadSavedFilter(filter);
                            closeSavedFiltersMenu();
                          }
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          py: 0.4,
                          px: 0.5,
                          borderRadius: 1,
                          cursor: isBusy ? 'default' : 'pointer',
                          bgcolor: isConfirmingDelete
                            ? '#fef2f2'
                            : isConfirmingOverwrite
                              ? `${colors.primary}10`
                              : isActive
                                ? `${colors.primary}08`
                                : 'transparent',
                          '&:hover': {
                            bgcolor: isConfirmingDelete
                              ? '#fef2f2'
                              : isConfirmingOverwrite
                                ? `${colors.primary}14`
                                : isActive
                                  ? `${colors.primary}12`
                                  : '#f9fafb',
                          },
                        }}
                      >
                        {isConfirmingDelete ? (
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Typography
                              sx={{
                                fontSize: '0.75rem',
                                color: colors.textSecondary,
                                flex: 1,
                                minWidth: 0,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              Delete &ldquo;{filter.name}&rdquo;?
                            </Typography>
                            <Button
                              size="small"
                              disabled={deletingFilterId === filter.id}
                              onClick={(e) => confirmDeleteFilter(filter.id, e)}
                              sx={{
                                textTransform: 'none',
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                py: 0,
                                px: 0.75,
                                minWidth: 0,
                                color: '#dc2626',
                                flexShrink: 0,
                                '&:hover': { bgcolor: '#fee2e2' },
                              }}
                            >
                              {deletingFilterId === filter.id ? '…' : 'Delete'}
                            </Button>
                            <Button
                              size="small"
                              disabled={deletingFilterId === filter.id}
                              onClick={cancelDeleteConfirm}
                              sx={{
                                textTransform: 'none',
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                py: 0,
                                px: 0.75,
                                minWidth: 0,
                                color: colors.textSecondary,
                                flexShrink: 0,
                              }}
                            >
                              Cancel
                            </Button>
                          </Box>
                        ) : isConfirmingOverwrite ? (
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Typography
                              sx={{
                                fontSize: '0.75rem',
                                color: colors.textSecondary,
                                flex: 1,
                                minWidth: 0,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              Overwrite &ldquo;{filter.name}&rdquo;?
                            </Typography>
                            <Button
                              size="small"
                              disabled={overwritingFilterId === filter.id}
                              onClick={(e) => confirmOverwriteFilter(filter.id, e)}
                              sx={{
                                textTransform: 'none',
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                py: 0,
                                px: 0.75,
                                minWidth: 0,
                                color: colors.primary,
                                flexShrink: 0,
                                '&:hover': { bgcolor: colors.primaryLight },
                              }}
                            >
                              {overwritingFilterId === filter.id ? '…' : 'Overwrite'}
                            </Button>
                            <Button
                              size="small"
                              disabled={overwritingFilterId === filter.id}
                              onClick={cancelOverwriteConfirm}
                              sx={{
                                textTransform: 'none',
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                py: 0,
                                px: 0.75,
                                minWidth: 0,
                                color: colors.textSecondary,
                                flexShrink: 0,
                              }}
                            >
                              Cancel
                            </Button>
                          </Box>
                        ) : isEditing ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                            <TextField
                              autoFocus
                              size="small"
                              fullWidth
                              value={editingFilterName}
                              onChange={(e) => setEditingFilterName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') commitEditFilterName();
                                if (e.key === 'Escape') cancelEditFilterName();
                              }}
                              onBlur={() => { setTimeout(() => commitEditFilterName(), 0); }}
                              onClick={(e) => e.stopPropagation()}
                              InputProps={{ sx: { fontSize: '0.8rem', height: 28, py: 0 } }}
                              sx={{ flex: 1 }}
                            />
                            <IconButton
                              size="small"
                              disabled={!editingFilterName.trim()}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={(e) => { e.stopPropagation(); commitEditFilterName(); }}
                              sx={{ color: colors.primary, p: 0.25 }}
                            >
                              <CheckIcon sx={{ fontSize: '0.85rem' }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={(e) => { e.stopPropagation(); cancelEditFilterName(); }}
                              sx={{ p: 0.25 }}
                            >
                              <CloseIcon sx={{ fontSize: '0.85rem' }} />
                            </IconButton>
                          </Box>
                        ) : (
                          <>
                            <Typography
                              sx={{
                                fontSize: '0.8rem',
                                fontWeight: isActive ? 700 : 500,
                                color: colors.text,
                                flex: 1,
                                minWidth: 0,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {filter.name}
                            </Typography>
                            <Box
                              className="saved-filter-actions"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.25,
                                flexShrink: 0,
                              }}
                            >
                              <Tooltip title="Overwrite with current filters">
                                <span>
                                  <Button
                                    size="small"
                                    onClick={(e) => startOverwriteConfirm(filter.id, e)}
                                    sx={{
                                      textTransform: 'none',
                                      fontSize: '0.65rem',
                                      fontWeight: 600,
                                      py: 0,
                                      px: 0.75,
                                      minWidth: 0,
                                      lineHeight: 1.4,
                                      color: colors.primary,
                                      flexShrink: 0,
                                      '&:hover': { bgcolor: colors.primaryLight },
                                    }}
                                  >
                                    Overwrite
                                  </Button>
                                </span>
                              </Tooltip>
                              <Tooltip title="Rename">
                                <IconButton
                                  size="small"
                                  onClick={(e) => startEditFilterName(filter, e)}
                                  sx={{ p: 0.25, color: colors.textSecondary }}
                                >
                                  <EditIcon sx={{ fontSize: '0.85rem' }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleDeleteFilter(filter.id, e)}
                                  sx={{ p: 0.25, color: colors.textSecondary }}
                                >
                                  <CloseIcon sx={{ fontSize: '0.85rem' }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}

              <Divider sx={{ my: 1 }} />

              {creatingSavedFilter ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TextField
                    autoFocus
                    size="small"
                    fullWidth
                    placeholder="Filter name"
                    value={newSavedFilterName}
                    onChange={(e) => setNewSavedFilterName(e.target.value)}
                    disabled={savingFilter}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveNewFilter();
                      if (e.key === 'Escape') { setCreatingSavedFilter(false); setNewSavedFilterName(''); }
                    }}
                    InputProps={{ sx: { fontSize: '0.8rem' } }}
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    size="small"
                    disabled={!newSavedFilterName.trim() || savingFilter}
                    onClick={handleSaveNewFilter}
                    sx={{ color: colors.primary }}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={savingFilter}
                    onClick={() => { setCreatingSavedFilter(false); setNewSavedFilterName(''); }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Button
                  size="small"
                  startIcon={<AddIcon sx={{ fontSize: '1rem !important' }} />}
                  onClick={() => setCreatingSavedFilter(true)}
                  sx={{
                    textTransform: 'none',
                    color: colors.primary,
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    p: 0.5,
                    justifyContent: 'flex-start',
                    '&:hover': { bgcolor: colors.primaryLight },
                  }}
                >
                  Save current filters
                </Button>
              )}
            </Box>
          </Popover>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: colors.textSecondary, p: 0.5, mr: -0.5, flexShrink: 0, ml: 'auto' }}>
            <KeyboardArrowDownIcon sx={{ transform: 'rotate(90deg)' }} fontSize="small" />
          </IconButton>
        </Box>
        {activeSavedFilter && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: filtersModified ? colors.primary : colors.textSecondary,
              fontSize: '0.7rem',
              lineHeight: 1.3,
              mt: 0.5,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {activeSavedFilter.name}{filtersModified ? ' · modified' : ''}
          </Typography>
        )}
      </Box>

      {/* Scrollable Content */}
      <Box ref={filterScrollRef} sx={{ flex: 1, overflowY: 'auto', p: 2, position: 'relative' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress size={28} sx={{ color: colors.primary }} />
          </Box>
        )}
        {!loading && <>
        {/* COUNTIES */}
        <FilterSection title="COUNTIES + MUNICIPALITIES">
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={locationFilterMode}
            onChange={(_, val) => val != null && onLocationFilterModeChange(val)}
            sx={{
              mb: 1.5,
              '& .MuiToggleButtonGroup-grouped': {
                borderColor: colors.border,
              },
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontSize: '0.68rem',
                fontWeight: 600,
                py: 0.5,
                px: 0.5,
                lineHeight: 1.2,
                color: colors.textSecondary,
                borderColor: colors.border,
              },
              // Selected outline sits above the neighbor so the shared edge stays purple.
              '& .MuiToggleButton-root.Mui-selected': {
                bgcolor: `${colors.primary}18`,
                color: colors.primary,
                borderColor: `${colors.primary} !important`,
                zIndex: 1,
              },
            }}
          >
            <ToggleButton value="counties">Counties</ToggleButton>
            <ToggleButton value="counties_and_municipalities">+ Municipalities</ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" sx={{ color: colors.textSecondary }}>
              {selectedCountySlugs.length} {selectedCountySlugs.length === 1 ? 'county' : 'counties'}
              {useMuniTree && totalMuniOptions > 0
                ? ` · ${selectedPublishedCount}/${totalMuniOptions} munis`
                : ''}
            </Typography>
            <Button size="small" onClick={handleSelectAllCounties} sx={{ fontSize: '0.7rem', minWidth: 0, p: 0, textTransform: 'none' }}>
              {selectedCountySlugs.length === counties.length ? 'Deselect All' : 'Select All'}
            </Button>
          </Box>
          <TextField
            fullWidth
            size="small"
            placeholder="Filter counties..."
            value={countySearch}
            onChange={(e) => setCountySearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              sx: { fontSize: '0.8rem', bgcolor: colors.surface, '& fieldset': { border: 'none' } }
            }}
            sx={{ mb: 1 }}
          />
          <Box sx={{ maxHeight: 218, overflowY: 'auto', mx: -1, px: 1 }}>
            {filteredCounties.map(c => {
              const munis = municipalityOptionsBySlug[c.slug] || [];
              const expanded = Boolean(expandedCountySlugs[c.slug]);
              if (!useMuniTree) {
                return (
                  <FormControlLabel
                    key={c.slug}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedCountySlugs.includes(c.slug)}
                        onChange={() => handleCountyToggle(c.slug)}
                        sx={{ color: colors.border, '&.Mui-checked': { color: colors.primary }, py: 0.5 }}
                      />
                    }
                    label={<Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{c.state ? `${c.name}, ${c.state}` : c.name}</Typography>}
                    sx={{ display: 'flex', m: 0 }}
                  />
                );
              }
              return (
                <Box key={c.slug} sx={{ mb: 0.25 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        width: 36,
                        flexShrink: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        pt: 0.25,
                      }}
                    >
                      {munis.length > 0 ? (
                        <IconButton
                          size="small"
                          aria-label={expanded ? 'Collapse municipalities' : 'Expand municipalities'}
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedCountySlugs((prev) => ({
                              ...prev,
                              [c.slug]: !prev[c.slug],
                            }));
                          }}
                          sx={{ p: 0.25, color: colors.textSecondary }}
                        >
                          {expanded ? (
                            <KeyboardArrowUpIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowDownIcon fontSize="small" />
                          )}
                        </IconButton>
                      ) : null}
                    </Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedCountySlugs.includes(c.slug)}
                          onChange={() => handleCountyToggle(c.slug)}
                          sx={{ color: colors.border, '&.Mui-checked': { color: colors.primary }, py: 0.5 }}
                        />
                      }
                      label={<Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{c.state ? `${c.name}, ${c.state}` : c.name}</Typography>}
                      sx={{ display: 'flex', m: 0, flex: 1, alignItems: 'center' }}
                    />
                  </Box>
                  {munis.length > 0 && (
                    <Collapse in={expanded}>
                      <Box sx={{ pl: 5.5, pr: 1, pb: 0.5 }}>
                        {munis.map((m) => {
                          const published = m.is_published !== false;
                          if (!published) {
                            return (
                              <Tooltip key={m.id} title="Not covered by LandSentry at this time" arrow placement="right">
                                <Box sx={{ display: 'flex', alignItems: 'center', m: 0, py: 0.25, opacity: 0.5 }}>
                                  <Box sx={{ width: 28, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                                    <CloseIcon sx={{ fontSize: '0.9rem', color: '#9ca3af' }} />
                                  </Box>
                                  <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#9ca3af', textDecoration: 'line-through' }}>
                                    {m.name}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            );
                          }
                          return (
                            <FormControlLabel
                              key={m.id}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={selectedMunicipalityIds.includes(m.id)}
                                  onChange={() => handleMunicipalityToggle(m.id)}
                                  sx={{
                                    color: colors.border,
                                    '&.Mui-checked': { color: colors.primary },
                                    py: 0.25,
                                  }}
                                />
                              }
                              label={
                                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: colors.textSecondary }}>
                                  {m.name}
                                </Typography>
                              }
                              sx={{ display: 'flex', m: 0 }}
                            />
                          );
                        })}
                      </Box>
                    </Collapse>
                  )}
                </Box>
              );
            })}
          </Box>
        </FilterSection>

        <Divider sx={{ my: 2 }} />

        {/* CASE TYPE */}
        <FilterSection title="CASE TYPE">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {caseTypes.map(type => (
              <PillButton
                key={type}
                label={type}
                selected={selectedCaseTypes.includes(type)}
                onClick={() => handleCaseTypeToggle(type)}
                color={APPLICATION_TYPES[type]?.labelColor || APPLICATION_TYPES[type]?.color || colors.primary}
              />
            ))}
          </Box>
        </FilterSection>

        <Divider sx={{ my: 2 }} />

        {/* DEAL SIZE */}
        <FilterSection title="DEAL SIZE">
          <DealSizeSlider
            label="Acres"
            value={dealSize?.acres?.value}
            operator={dealSize?.acres?.operator}
            onChange={(val) => onDealSizeChange({ ...dealSize, acres: val })}
            max={500}
          />
          <DealSizeSlider
            label="Units"
            value={dealSize?.units?.value}
            operator={dealSize?.units?.operator}
            onChange={(val) => onDealSizeChange({ ...dealSize, units: val })}
            max={1000}
          />
          <DealSizeSlider
            label="Proposed density (du/ac)"
            value={dealSize?.density?.value}
            operator={dealSize?.density?.operator}
            onChange={(val) => onDealSizeChange({ ...dealSize, density: val })}
            max={50}
          />
        </FilterSection>

        <Divider sx={{ my: 2 }} />

        {/* STATUS (case status) — 4 group buttons + sub-filters */}
        <FilterSection title="STATUS">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {Object.entries(STATUS_FILTER_GROUPS).map(([groupKey, groupStatuses]) => {
              const allSelected = groupStatuses.every((s) => selectedStatuses.includes(s));
              const groupColor = getCaseStatusColor(groupKey);
              const hasSubs = groupStatuses.length > 1;
              return (
                <React.Fragment key={groupKey}>
                  <PillButton
                    label={groupKey}
                    selected={allSelected}
                    onClick={() => handleStatusGroupToggle(groupKey)}
                    color={groupColor}
                  />
                  {hasSubs && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
                      {groupStatuses.filter((s) => s !== groupKey).map((st) => {
                        const on = selectedStatuses.includes(st);
                        const c = getCaseStatusColor(st);
                        return (
                          <Button
                            key={st}
                            variant={on ? 'contained' : 'outlined'}
                            onClick={() => handleStatusToggle(st)}
                            sx={{
                              borderRadius: '20px',
                              textTransform: 'none',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              py: 0.4,
                              px: 1,
                              bgcolor: on ? c : 'transparent',
                              color: on ? '#fff' : colors.textSecondary,
                              borderColor: on ? c : colors.border,
                              '&:hover': {
                                bgcolor: on ? c : colors.primaryLight,
                                borderColor: c,
                              },
                            }}
                          >
                            {STATUS_BADGE_LABELS[st]}
                          </Button>
                        );
                      })}
                    </Box>
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        </FilterSection>

        <Divider sx={{ my: 2 }} />

        {/* DATE RANGE — overlap uses first_activity_date & last_activity_date (timeline fallback) */}
        <FilterSection title="DATE RANGE">
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={includeAllDates}
                onChange={(e) => onDateChange({ includeAllDates: e.target.checked, from: dateRange.from, to: dateRange.to })}
                sx={{ color: colors.border, '&.Mui-checked': { color: colors.primary } }}
              />
            }
            label={<Typography variant="body2" sx={{ fontSize: '0.8rem' }}>All Time</Typography>}
          />
          {!includeAllDates && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1 }}>
              <TextField
                label="From"
                type="date"
                size="small"
                value={localDateRange.from || ''}
                onChange={(e) => setLocalDateRange(prev => ({ ...prev, from: e.target.value }))}
                onBlur={handleDateBlur}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
              />
              <TextField
                label="To"
                type="date"
                size="small"
                value={localDateRange.to || ''}
                onChange={(e) => setLocalDateRange(prev => ({ ...prev, to: e.target.value }))}
                onBlur={handleDateBlur}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
              />
              {onActivityDateModeChange && (
                <Box>
                  <Typography variant="caption" sx={{ display: 'block', color: colors.textSecondary, fontWeight: 600, mb: 0.75 }}>
                    Match by
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    fullWidth
                    size="small"
                    value={activityDateMode}
                    onChange={(_, val) => val != null && onActivityDateModeChange(val)}
                    sx={{
                      '& .MuiToggleButtonGroup-grouped': {
                        borderColor: colors.border,
                      },
                      '& .MuiToggleButton-root': {
                        textTransform: 'none',
                        fontSize: '0.68rem',
                        fontWeight: 600,
                        py: 0.6,
                        px: 0.5,
                        lineHeight: 1.2,
                        color: colors.textSecondary,
                        borderColor: colors.border,
                      },
                      '& .MuiToggleButton-root.Mui-selected': {
                        bgcolor: `${colors.primary}18`,
                        color: colors.primary,
                        borderColor: `${colors.primary} !important`,
                        zIndex: 1,
                      },
                    }}
                  >
                    <Tooltip title="First activity date falls inside the range (inclusive)">
                      <ToggleButton value="first">First activity</ToggleButton>
                    </Tooltip>
                    <Tooltip title="Case had any activity between first and last dates that overlaps the range (inclusive)">
                      <ToggleButton value="overlap">All activity</ToggleButton>
                    </Tooltip>
                    <Tooltip title="Last activity date falls inside the range (inclusive)">
                      <ToggleButton value="last">Last activity</ToggleButton>
                    </Tooltip>
                  </ToggleButtonGroup>
                </Box>
              )}
            </Box>
          )}
        </FilterSection>

        <Divider sx={{ my: 2 }} />

        <FilterSection title="CASE NATURE" defaultOpen={false}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {(caseNatureOptions || []).map((nature) => (
              <PillButton
                key={nature}
                label={nature}
                selected={selectedCaseNatures.includes(nature)}
                onClick={() => handleCaseNatureToggle(nature)}
                color={nature === 'Appealed Case' ? '#ea580c' : colors.primary}
              />
            ))}
          </Box>
        </FilterSection>

        </>}
      </Box>
    </Box>
  );
}
